import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError, API_BASE } from "../lib/api";
import { fmtCLP, fmtDate, currentMonthISO } from "../lib/format";
import { toast } from "sonner";
import { FileDown, ArrowRight, CheckCircle2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MESES_LARGO = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const VERDE = "#1A3328";
const DORADO = "#C9AE7A";
const ROJO = "#A94438";

const CATEGORIA_LABELS = {
  costo_ventas: "Costo de ventas",
  gastos_admin: "Gastos de administración",
  gastos_ventas: "Gastos comerciales",
  gastos_fin: "Gastos financieros",
  impuestos: "Impuestos",
  otros_egresos: "Otros egresos",
};

const REGIMEN_TASA = {
  "14D3": "Pro Pyme General",
  "14D8": "Pro Pyme Transparente",
  "14A": "Semi Integrado",
};

// Eje adaptable: $850, $50K, $119K, $1,2M, $25M
function fmtCompacto(v) {
  const a = Math.abs(v || 0);
  const s = v < 0 ? "-" : "";
  if (a >= 1e9) return `${s}$${(a / 1e9).toLocaleString("es-CL", { maximumFractionDigits: 1 })} mil M`;
  if (a >= 1e6) return `${s}$${(a / 1e6).toLocaleString("es-CL", { maximumFractionDigits: a >= 1e7 ? 0 : 1 })}M`;
  if (a >= 1e3) return `${s}$${Math.round(a / 1e3)}K`;
  return `${s}$${Math.round(a)}`;
}

const opcionesMes = (seleccionado) => {
  const hoy = new Date();
  const out = [];
  for (let n = 0; n < 36; n++) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() - n, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const nombre = MESES_LARGO[d.getMonth()];
    out.push({ value, label: `${nombre[0].toUpperCase()}${nombre.slice(1)} ${d.getFullYear()}` });
  }
  if (seleccionado && !out.some((o) => o.value === seleccionado)) {
    const [y, m] = seleccionado.split("-").map(Number);
    const nombre = MESES_LARGO[m - 1];
    out.push({ value: seleccionado, label: `${nombre[0].toUpperCase()}${nombre.slice(1)} ${y}` });
  }
  return out;
};

const mesCorto = (yyyymm) => MESES[parseInt(yyyymm.split("-")[1], 10) - 1];

export default function Dashboard() {
  const [mes, setMes] = useState(currentMonthISO);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard/ejecutivo", { params: { mes } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  }, [mes]);

  const downloadPdf = () => {
    window.open(`${API_BASE}/dashboard/ejecutivo/pdf?mes=${mes}`, "_blank");
  };

  if (loading && !data) return <Page><div className="p-12 text-center text-muted-foreground">Generando informe ejecutivo…</div></Page>;
  if (!data) return <Page><div className="p-12 text-center text-muted-foreground">No se pudo cargar el informe.</div></Page>;

  const k = data.kpis;
  const c = data.comparativas;
  const i = data.impuestos;
  const r = data.ratios;
  const mesNum = parseInt(data.periodo.mes_param.split("-")[1], 10);
  const anioAnt = parseInt(data.periodo.mes_param.split("-")[0], 10) - 1;
  const parcial = data.periodo.comparacion_parcial ? `1–${data.periodo.dia_corte} ` : "";
  const vsMes = parcial ? `vs ${parcial}${MESES[(mesNum + 10) % 12].toLowerCase()}` : "vs mes ant.";
  const vsAnio = `vs ${parcial}${MESES[mesNum - 1].toLowerCase()} ${anioAnt}`;
  const generado = new Date(data.periodo.generado);
  const kicker = [data.empresa?.nombre, data.empresa?.regimen_label ? `Régimen ${data.empresa.regimen_label}` : null].filter(Boolean).join(" · ");

  return (
    <Page>
      <PageHeader
        kicker={kicker || "Resumen"}
        title="Informe Ejecutivo"
        subtitle={`${data.periodo.mes_label} · datos al ${fmtDate(generado.toISOString())}, ${generado.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}`}
        testid="dash-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={mes}
              onChange={(e) => setMes(e.target.value)}
              data-testid="dash-mes"
              aria-label="Período"
              className="px-3 py-2 border border-border rounded-sm bg-card focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
            >
              {opcionesMes(mes).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button onClick={downloadPdf} data-testid="dash-pdf"
              className="inline-flex items-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm">
              <FileDown className="h-4 w-4" /> Descargar PDF
            </button>
          </div>
        }
      />

      <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
        <Atencion alertas={data.alertas || []} />

        {/* RESULTADOS DEL MES */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5" data-testid="dash-kpis">
          <Kpi label="Ingresos netos" value={fmtCLP(k.ingresos_mes)} cmp={c.ingresos} vsMes={vsMes} vsAnio={vsAnio} fuente="Libro diario · cuentas de ingreso, sin IVA" />
          <Kpi label="Egresos" value={fmtCLP(k.egresos_mes)} cmp={c.egresos} vsMes={vsMes} vsAnio={vsAnio} inverso fuente="Libro diario · costos y gastos" />
          <Kpi label="Utilidad del mes" value={fmtCLP(k.utilidad_mes)} cmp={c.utilidad} vsMes={vsMes} vsAnio={vsAnio}
            tono={k.utilidad_mes < 0 ? "text-destructive" : undefined} fuente="Ingresos − egresos" />
          <Kpi label="Margen" value={k.margen_neto_mes_pct !== null ? `${k.margen_neto_mes_pct.toLocaleString("es-CL")} %` : "—"}
            cmp={c.margen} vsMes={vsMes} vsAnio={vsAnio} puntos fuente="Utilidad ÷ ingresos netos" />
        </div>

        {/* LIQUIDEZ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <div className="bg-primary text-primary-foreground rounded-md p-5 flex flex-col" data-testid="dash-liquidez">
            <div className="label-caps !text-primary-foreground/80">Liquidez</div>
            <div className="text-[12px] text-primary-foreground/70 mt-3">Saldo en caja y bancos</div>
            <div className={`num text-3xl font-medium tracking-tight ${k.saldo_bancos < 0 ? "text-red-200" : ""}`}>{fmtCLP(k.saldo_bancos)}</div>
            <div className="text-[10px] text-primary-foreground/55 mt-1">Contabilidad · mismas cuentas que el Balance (1.1.01 / 1.1.02)</div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <div className="text-[11px] text-primary-foreground/70">Razón corriente</div>
                <div className="num text-lg font-semibold">{r.razon_corriente !== null ? r.razon_corriente.toLocaleString("es-CL", { maximumFractionDigits: 2 }) : "—"}</div>
              </div>
              <div>
                <div className="text-[11px] text-primary-foreground/70">Capital de trabajo</div>
                <div className="num text-lg font-semibold">{fmtCLP(r.capital_trabajo)}</div>
              </div>
            </div>
            <div className="text-[11px] text-primary-foreground/60 mt-auto pt-4">
              Fuente: {data.liquidez?.fuente || "contabilidad"}
              {data.liquidez?.ultima_cartola ? ` · última cartola ${fmtDate(data.liquidez.ultima_cartola)}` : ""}
            </div>
          </div>
          <Antiguedad titulo="Por cobrar" a={data.cxc} to="/app/cuenta-corriente" fuente="Facturas emitidas con saldo pendiente" testid="dash-cxc" />
          <Antiguedad titulo="Por pagar" a={data.cxp} to="/app/pagos-proveedores" fuente="Facturas recibidas con saldo pendiente" testid="dash-cxp" />
        </div>

        {/* IMPUESTOS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <F29Card i={i} />
          <RentaCard i={i} />
        </div>

        {/* TENDENCIA */}
        <Tendencia trend={data.trend_12m} actual={data.periodo.mes_param} />

        {/* RANKINGS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <Ranking titulo="Top 5 clientes · año" items={data.top_clientes} color={VERDE} fuente="Libro de ventas · montos netos" testid="dash-top-clientes" />
          <Ranking titulo="Top 5 proveedores · año" items={data.top_proveedores} color={DORADO} fuente="Libro de compras · montos netos" testid="dash-top-prov" />
          <Gastos items={data.gastos_categoria} periodo={data.gastos_periodo} mesLabel={MESES_LARGO[mesNum - 1]} />
        </div>
      </div>
    </Page>
  );
}

// --- Subcomponentes ---
const Card = ({ children, className = "", testid }) => (
  <div className={`bg-card border border-border rounded-md p-5 ${className}`} data-testid={testid}>{children}</div>
);

const Fuente = ({ children }) => <div className="text-[11px] text-muted-foreground mt-3">Fuente: {children}</div>;

const SEVERIDAD = {
  danger: { tile: "bg-destructive/10 border-destructive/30", dot: "bg-destructive" },
  warning: { tile: "bg-warning-surface border-warning/40", dot: "bg-warning" },
  info: { tile: "bg-muted/60 border-border", dot: "bg-gold" },
};

function Atencion({ alertas }) {
  return (
    <Card className="mb-5" testid="dash-alertas">
      <div className="label-caps mb-3">Requiere atención · {alertas.length}</div>
      {alertas.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-success" /> Sin pendientes tributarios ni de facturación.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {alertas.map((a, idx) => {
            const s = SEVERIDAD[a.severidad] || SEVERIDAD.info;
            return (
              <div key={idx} className={`border rounded-sm p-3 flex flex-col ${s.tile}`} data-testid={`alert-${a.tipo}`}>
                <div className="flex items-start gap-2">
                  <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${s.dot}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground leading-snug">{a.titulo}</div>
                    <div className="text-[12px] text-muted-foreground mt-0.5 leading-snug">{a.mensaje}</div>
                  </div>
                </div>
                {a.accion && (
                  <Link to={a.accion.to} className="self-start ml-4 mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-primary hover:underline">
                    {a.accion.label} <ArrowRight className="h-3 w-3" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function Delta({ d, inverso, puntos, etiqueta }) {
  if (d === null || d === undefined) return <span className="text-muted-foreground/70">— {etiqueta}</span>;
  const bueno = inverso ? d < 0 : d > 0;
  const color = d === 0 ? "text-muted-foreground" : bueno ? "text-success" : "text-destructive";
  const signo = d > 0 ? "▲ +" : d < 0 ? "▼ " : "";
  return (
    <span className={color}>
      {signo}{d.toLocaleString("es-CL")}{puntos ? " pts" : " %"} <span className="text-muted-foreground">{etiqueta}</span>
    </span>
  );
}

function Kpi({ label, value, cmp, vsMes, vsAnio, inverso, puntos, tono, fuente }) {
  return (
    <Card>
      <div className="label-caps">{label}</div>
      <div className={`num text-2xl sm:text-[1.7rem] tracking-tight font-medium mt-2 ${tono || "text-foreground"}`}>{value}</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11.5px]">
        <Delta d={cmp?.delta_mes} inverso={inverso} puntos={puntos} etiqueta={vsMes} />
        <Delta d={cmp?.delta_ano} inverso={inverso} puntos={puntos} etiqueta={vsAnio} />
      </div>
      <Fuente>{fuente}</Fuente>
    </Card>
  );
}

function Antiguedad({ titulo, a, to, fuente, testid }) {
  const total = a?.total || 0;
  const tramos = [
    { k: "d0_30", label: "0–30 días", color: VERDE },
    { k: "d31_60", label: "31–60 días", color: DORADO },
    { k: "d60", label: "+60 días", color: ROJO },
  ];
  return (
    <Card testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-caps">{titulo}</div>
        <Link to={to} className="text-[12px] font-semibold text-primary hover:underline">Ver detalle</Link>
      </div>
      <div className="num text-2xl font-medium tracking-tight mt-2">{fmtCLP(total)}</div>
      <div className="flex h-2 rounded-sm overflow-hidden bg-muted mt-3">
        {total > 0 && tramos.map((t) => (
          <div key={t.k} style={{ width: `${(a[t.k] / total) * 100}%`, backgroundColor: t.color }} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2">
        {tramos.map((t) => (
          <div key={t.k}>
            <div className={`text-[11px] ${t.k === "d60" && a?.[t.k] > 0 ? "text-destructive" : "text-muted-foreground"}`}>{t.label}</div>
            <div className={`num text-[13px] font-semibold ${t.k === "d60" && a?.[t.k] > 0 ? "text-destructive" : ""}`}>{fmtCLP(a?.[t.k] || 0)}</div>
          </div>
        ))}
      </div>
      <Fuente>{fuente} · {a?.documentos || 0} {a?.documentos === 1 ? "documento" : "documentos"}</Fuente>
    </Card>
  );
}

function Fila({ label, value, fuerte, tono }) {
  return (
    <div className={`flex justify-between gap-3 py-1.5 text-sm ${fuerte ? "font-semibold border-t border-border mt-1 pt-2.5" : "border-b border-border/60"}`}>
      <span className={fuerte ? "text-foreground" : "text-muted-foreground"}>{label}</span>
      <span className={`num font-mono ${tono || "text-foreground"}`}>{value}</span>
    </div>
  );
}

function EstadoVencimiento({ fecha, dias }) {
  if (!fecha) return null;
  const [y, m, d] = fecha.split("-");
  const corta = `${d}-${m}`;
  let texto = `Vence ${corta} · en ${dias} días`;
  let cls = "bg-gold/15 text-foreground";
  if (dias < 0) {
    texto = `Plazo vencido el ${corta}-${y}`;
    cls = "bg-muted text-muted-foreground";
  } else if (dias === 0) {
    texto = "Vence hoy";
    cls = "bg-destructive/15 text-destructive";
  } else if (dias === 1) {
    texto = `Vence mañana (${corta})`;
    cls = "bg-destructive/15 text-destructive";
  } else if (dias <= 5) {
    cls = "bg-warning-surface text-warning-foreground";
  }
  return <span className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${cls}`} data-testid="dash-f29-estado">{texto}</span>;
}

function F29Card({ i }) {
  const total = i.f29_total_estimado ?? Math.max(i.iva_a_pagar_mes, 0) + i.ppm_renta_mes;
  return (
    <Card testid="dash-iva">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-xl">F29 · {i.periodo_libros_label}</h3>
          <div className="text-[12px] text-muted-foreground">IVA, PPM y retenciones</div>
        </div>
        <EstadoVencimiento fecha={i.f29_vencimiento} dias={i.f29_dias} />
      </div>
      <div className="mt-3">
        <Fila label={`IVA débito (${i.doc_ventas} ${i.doc_ventas === 1 ? "venta" : "ventas"})`} value={fmtCLP(i.iva_debito_mes)} />
        <Fila label={`IVA crédito (${i.doc_compras} ${i.doc_compras === 1 ? "compra" : "compras"})`} value={`− ${fmtCLP(i.iva_credito_mes)}`} />
        {(i.remanente_anterior || 0) > 0 && (
          <Fila label="Remanente anterior (crédito arrastrado)" value={`− ${fmtCLP(i.remanente_anterior)}`} tono="text-success" />
        )}
        {((i.remanente_mes || 0) > 0 || i.iva_a_pagar_mes < 0) ? (
          <Fila
            label="Remanente de crédito al mes siguiente"
            value={fmtCLP(i.remanente_mes ?? Math.max(0, -i.iva_a_pagar_mes))}
            tono="text-success"
          />
        ) : null}
        <Fila label={`PPM (${(i.tasa_ppm_pct ?? 0).toLocaleString("es-CL")} % de las ventas netas)`} value={fmtCLP(i.ppm_renta_mes)} />
        <Fila label={`Retención honorarios (${(i.tasa_retencion_honorarios_pct ?? 15.25).toLocaleString("es-CL")} %)`} value={fmtCLP(i.retencion_honorarios_mes ?? 0)} />
        <Fila label="Impuesto único (remuneraciones)" value={fmtCLP(i.impuesto_unico_mes ?? 0)} />
        <Fila label="Total a pagar" value={fmtCLP(total)} fuerte />
      </div>
      <Fuente>libros de compra y venta, honorarios y remuneraciones · plazo día {i.f29_dia_plazo || 20}, corrido al día hábil siguiente si cae en fin de semana o feriado</Fuente>
    </Card>
  );
}

function RentaCard({ i }) {
  const rt = i.renta || {};
  const transparente = rt.regimen === "14D8";
  const saldo = rt.saldo ?? i.impuesto_renta_proyectado_anual;
  return (
    <Card testid="dash-renta">
      <h3 className="font-display text-xl">Impuesto a la renta · AT {rt.anio_tributario}</h3>
      <div className="text-[12px] text-muted-foreground">
        Estimación (no es RLI ni F22) · régimen {REGIMEN_TASA[rt.regimen] || "Pro Pyme General"}
        {!rt.regimen_configurado && (
          <> · <Link to="/app/empresas" className="text-primary font-semibold hover:underline">configurar régimen</Link></>
        )}
      </div>
      <div className="mt-3">
        <Fila label="Utilidad del año a la fecha (sin IVA)" value={fmtCLP(rt.utilidad_ytd)} />
        <Fila label={`Utilidad anualizada (${(rt.meses_transcurridos || 0).toLocaleString("es-CL", { maximumFractionDigits: 1 })} meses → 12)`} value={fmtCLP(rt.utilidad_anualizada)} />
        <Fila label="Tasa del régimen" value={`${(rt.tasa_pct ?? 0).toLocaleString("es-CL")} %`} />
        <Fila label="Impuesto anual estimado" value={fmtCLP(rt.impuesto)} />
        <Fila label="PPM pagados a la fecha" value={`− ${fmtCLP(rt.ppm_pagados)}`} />
        <Fila label="PPM que faltan en el año (estimado)" value={`− ${fmtCLP(rt.ppm_proyectados)}`} />
        <Fila label={saldo < 0 ? "Saldo estimado a devolver" : "Saldo estimado a pagar"} value={fmtCLP(Math.abs(saldo))}
          fuerte tono={saldo < 0 ? "text-success" : undefined} />
      </div>
      <div className="text-[11px] text-muted-foreground mt-3">
        {transparente
          ? "En el régimen transparente la empresa no paga impuesto de primera categoría: tributan los dueños. "
          : "Estimación referencial sobre utilidad contable anualizada (no sustituye la RLI del F22). "}
        Fuente: libro mayor y libro de ventas.
      </div>
    </Card>
  );
}

function Tendencia({ trend, actual }) {
  const datos = (trend || []).map((t) => ({ ...t, label: mesCorto(t.mes), actual: t.mes === actual }));
  const vacio = !datos.some((t) => t.ingresos || t.egresos);
  return (
    <Card className="mb-5" testid="dash-tendencia">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display text-xl">Tendencia 12 meses</h3>
          <div className="text-[12px] text-muted-foreground">Ingresos y egresos netos por mes · el mes consultado destacado</div>
        </div>
      </div>
      {vacio ? (
        <div className="text-sm text-muted-foreground text-center py-12">Sin movimientos contables en los últimos 12 meses.</div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={datos} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompacto} width={56} tickLine={false} axisLine={false} />
            <Tooltip formatter={(v) => fmtCLP(v)} cursor={{ fill: "rgba(26,51,40,0.05)" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="ingresos" name="Ingresos" fill={VERDE} radius={[2, 2, 0, 0]}>
              {datos.map((t) => <Cell key={t.mes} fill={VERDE} fillOpacity={t.actual ? 1 : 0.75} />)}
            </Bar>
            <Bar dataKey="egresos" name="Egresos" fill={DORADO} radius={[2, 2, 0, 0]}>
              {datos.map((t) => <Cell key={t.mes} fill={DORADO} fillOpacity={t.actual ? 1 : 0.75} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      <Fuente>libro diario (asientos registrados)</Fuente>
    </Card>
  );
}

function Ranking({ titulo, items, color, fuente, testid }) {
  const max = items?.[0]?.total || 1;
  return (
    <Card testid={testid}>
      <div className="label-caps mb-3">{titulo}</div>
      {items?.length ? (
        <ul className="space-y-2.5">
          {items.map((it, idx) => (
            <li key={idx} className="text-[12.5px]">
              <div className="flex justify-between gap-2 mb-1">
                <span className="truncate" title={it.nombre}>{it.nombre}</span>
                <span className="num font-mono font-semibold">{fmtCLP(it.total)}</span>
              </div>
              <div className="h-1.5 bg-muted rounded-sm overflow-hidden">
                <div className="h-full rounded-sm" style={{ width: `${(it.total / max) * 100}%`, backgroundColor: color }} />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-muted-foreground text-center py-6">Sin documentos en el año.</div>
      )}
      <Fuente>{fuente}</Fuente>
    </Card>
  );
}

function Gastos({ items, periodo, mesLabel }) {
  const total = (items || []).reduce((a, g) => a + g.monto, 0);
  return (
    <Card testid="dash-gastos">
      <div className="label-caps mb-3">Distribución de gastos · {periodo === "ytd" ? "año" : mesLabel}</div>
      {total > 0 ? (
        <ul className="space-y-2.5">
          {items.map((g) => {
            const pct = (g.monto / total) * 100;
            return (
              <li key={g.categoria} className="text-[12.5px]">
                <div className="flex justify-between gap-2 mb-1">
                  <span className="truncate">{CATEGORIA_LABELS[g.categoria] || g.categoria}</span>
                  <span className="num font-semibold" title={fmtCLP(g.monto)}>{pct.toLocaleString("es-CL", { maximumFractionDigits: 0 })} %</span>
                </div>
                <div className="h-1.5 bg-muted rounded-sm overflow-hidden">
                  <div className="h-full rounded-sm" style={{ width: `${pct}%`, backgroundColor: "#7A5028" }} />
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="text-sm text-muted-foreground text-center py-6">Sin gastos registrados.</div>
      )}
      <Fuente>libro mayor, cuentas de egreso{periodo === "ytd" ? " · el mes no tiene gastos, se muestra el año" : ""}</Fuente>
    </Card>
  );
}
